// Production-ready useStream hook for FastAPI backend with SSE streaming
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { Client } from "../lib/client";
import { Message } from "../types/sdk";
import type { UseStreamOptions } from "../types/streaming";
import type { UIMessage } from "@/types/ui";
import { storageManager } from "@/lib/sessionStorageManager";
import { STORAGE_CONFIG } from "@/config";
import { type AddressData } from "@/services/conversation-service";

// Re-export types for backward compatibility
export type { UseStreamOptions } from "../types/streaming";

interface MessageMetadata {
  firstSeenState?: {
    messages?: Message[];
    ui?: UIMessage[];
    context?: Record<string, unknown>;
    parent_checkpoint?: any;
  };
  branch?: string;
  branchOptions?: string[];
}

/**
 * Strip large attachment data from messages before storing in sessionStorage
 * to avoid QuotaExceededError. Keeps metadata and thumbnails but removes full base64 data.
 */
function stripAttachmentsFromMessages(messages: Message[]): Message[] {
  return messages.map((msg) => {
    // If message has content array with attachments, strip the data
    if (Array.isArray(msg.content)) {
      const strippedContent = msg.content.map((block: any) => {
        // Strip ONLY full-size base64 data, keep thumbnails (they're small)
        if (
          (block.type === "document" || block.type === "image") &&
          block.source?.type === "base64" &&
          block.source?.data &&
          block.source.data !== "[STRIPPED]"
        ) {
          return {
            ...block,
            source: {
              ...block.source,
              data: "[STRIPPED]", // Replace large base64 with placeholder
            },
            // Keep thumbnail field (it's small, ~40-80 KB)
          };
        }
        return block;
      });

      return {
        ...msg,
        content: strippedContent,
      };
    }

    return msg;
  });
}

export function useStreamWithBackend(options: UseStreamOptions) {
  const {
    userId,
    threadId: externalThreadId,
    fetchStateHistory = false,
    onCustomEvent,
    onThreadId,
    onClearThreadId,
    onStatusChange,
    threadMetadata,
  } = options;

  // State
  // messages is the SINGLE SOURCE OF TRUTH for message data
  // ui and context are separate state (no values wrapper)
  const [messages, setMessages] = useState<Message[]>([]);
  const [ui, setUi] = useState<UIMessage[]>([]);
  const [context, setContext] = useState<Record<string, unknown>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Track submit flow start (before async work)
  const [error, setError] = useState<Error | null>(null);
  const [interrupt, setInterrupt] = useState<any>(null);
  const [hasActiveStream, setHasActiveStream] = useState(false);

  // Refs
  const clientRef = useRef<Client | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const internalThreadIdRef = useRef<string | null>(null);
  const messagesMetadataRef = useRef<Map<string, MessageMetadata>>(new Map());

  // Get or create client
  const getClient = useCallback(() => {
    if (!clientRef.current) {
      clientRef.current = new Client({
        userId,
      });
    }
    return clientRef.current;
  }, [userId]);

  // Track which thread ID the current messages belong to
  const [messagesThreadId, setMessagesThreadId] = useState<string | null>(null);

  // Track previous threadId to detect thread switches
  const prevThreadIdRef = useRef<string | null>(null);

  // Track the target thread ID for the current stream
  // Used to verify stream data belongs to the expected thread and prevent stale updates
  const targetThreadIdRef = useRef<string | null>(null);

  // Get effective thread ID
  const getThreadId = useCallback(() => {
    return externalThreadId || internalThreadIdRef.current;
  }, [externalThreadId]);

  // Cancel stream when switching to a different thread
  // This prevents old streams from continuing to run after thread switch
  useEffect(() => {
    // Only cancel if we're switching FROM one thread TO another (not initial mount)
    if (prevThreadIdRef.current !== null &&
        externalThreadId !== null &&
        prevThreadIdRef.current !== externalThreadId &&
        abortControllerRef.current) {
      console.log(`[useStreamWithBackend] Thread switch detected (${prevThreadIdRef.current} → ${externalThreadId}), cancelling previous stream`);
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
      setIsSubmitting(false);
      setHasActiveStream(false);
    }
    prevThreadIdRef.current = externalThreadId;
  }, [externalThreadId]);

  // Fetch history on mount or thread change
  useEffect(() => {
    // Load history when threadId changes AND it's different from current messages' thread
    // This ensures we load messages when switching threads, even if messages array is not empty
    // IMPORTANT: Don't load history if there's an active stream or submit in progress (race condition fix)
    if (
      fetchStateHistory &&
      externalThreadId &&
      userId &&
      externalThreadId !== messagesThreadId &&
      !hasActiveStream &&  // Don't interrupt active streams
      !isSubmitting  // Don't interrupt submit flow (capture mode)
    ) {
      const loadHistory = async () => {
        // Check if a submit is already in progress (abortController exists)
        // If so, don't touch isLoading - let submit() control it
        const submitInProgress = abortControllerRef.current !== null;

        try {
          if (!submitInProgress) {
            console.log('[useStreamWithBackend] loadHistory() - setting isLoading=true (no submit in progress)');
            setIsLoading(true);
          } else {
            console.log('[useStreamWithBackend] loadHistory() - NOT setting isLoading (submit already in progress)');
          }

          // Try to load from session storage first (using LRU cache manager)
          const cachedMessages = storageManager.getThreadMessages(externalThreadId);

          if (cachedMessages && Array.isArray(cachedMessages) && cachedMessages.length > 0) {
            console.log('[useStreamWithBackend] Found cached messages, loading from cache');
            setMessages(cachedMessages);
            setMessagesThreadId(externalThreadId); // Track which thread these messages belong to
            setError(null);
            setIsLoading(false); // Reset loading state after cache load
            return; // Skip Firebase query
          }

          // No session storage or parse failed - load from Firebase
          const client = getClient();
          const historyMessages =
            await client.getConversationHistory(externalThreadId);

          // Update messages for the new thread
          setMessages(historyMessages);
          setMessagesThreadId(externalThreadId); // Track which thread these messages belong to

          // NOTE: Stream reconnection is now handled by useStreamSession.ts via useAgentStatus hook
          // When agentStatus is RUNNING_AGENT_LOOP, it triggers submit() to reconnect to the SSE stream.
          // This avoids needing a backend endpoint for project details since Firestore is read directly.

          // Cache messages in session storage for next reload (strip attachments to avoid quota)
          if (historyMessages.length > 0) {
            const messagesWithoutAttachments = stripAttachmentsFromMessages(historyMessages);
            const success = storageManager.setThreadMessages(
              externalThreadId,
              messagesWithoutAttachments
            );
            if (success && STORAGE_CONFIG.ENABLE_STORAGE_WARNINGS) {
              console.log(
                `💾 Cached ${historyMessages.length} messages to session storage (attachments stripped)`,
              );
            }
          }

          setError(null);
          // Reset loading state after successful Firebase load
          if (!submitInProgress) {
            setIsLoading(false);
          }
        } catch (err) {
          console.error("Failed to fetch history:", err);

          // If thread not found (404), clear the threadId from URL and start fresh
          if (err instanceof Error && err.message.includes("Not Found")) {
            console.warn(
              "Thread not found in backend, clearing invalid threadId",
            );
            // Clear messages and reset thread tracking
            setMessages([]);
            setUi([]);
            setContext({});
            setMessagesThreadId(null); // Clear thread ID tracking
            // Don't set error - just start fresh
            setError(null);

            // Clear the internal thread ID ref so a new thread will be created
            internalThreadIdRef.current = null;

            // Clear the threadId from URL
            if (onClearThreadId) {
              onClearThreadId();
            }

            console.info(
              "Thread no longer exists in backend - will create new thread on next message",
            );
          } else {
            setError(
              err instanceof Error ? err : new Error("Failed to fetch history"),
            );
          }
          // Only set isLoading=false on error if we set it to true earlier
          if (!submitInProgress) {
            setIsLoading(false);
          }
        }
      };

      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalThreadId, fetchStateHistory, getClient, userId, messagesThreadId, hasActiveStream, isSubmitting]);

  // Submit method - send messages and stream response
  const submit = useCallback(
    async (input: any, config?: any) => {
      // Set submitting state IMMEDIATELY - before any async work
      // This prevents history loading from interfering with the submit flow
      setIsSubmitting(true);

      try {
        // Cancel any existing stream
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        console.log('[useStreamWithBackend] submit() called - setting isLoading=true, isSubmitting=true');
        setIsLoading(true);
        setHasActiveStream(true); // Mark stream as active IMMEDIATELY to prevent flash
        setError(null);

        const client = getClient();

        // Check if we should force a new thread (ignore existing threadId)
        const forceNewThread = config?.forceNewThread === true;
        let currentThreadId = forceNewThread ? null : getThreadId();

        // Set target thread ID for this stream session
        // This is used to verify incoming data belongs to the expected thread
        targetThreadIdRef.current = currentThreadId;

        // Create thread if needed OR if forced
        let isNewThread = false;
        if (!currentThreadId && !abortController.signal.aborted) {
          if (!threadMetadata) {
            throw new Error("Cannot create thread: threadMetadata is required");
          }
          try {
            const createThreadGen = client.createThread(threadMetadata);

            for await (const result of createThreadGen) {
              if (abortController.signal.aborted) {
                break;
              }

              // First result contains threadId
              if (result.threadId && !currentThreadId) {
                currentThreadId = result.threadId;
                internalThreadIdRef.current = currentThreadId;
                targetThreadIdRef.current = currentThreadId; // Update target for new thread
                isNewThread = true;

                if (onThreadId) {
                  onThreadId(currentThreadId);
                }

              }

              // Handle welcome message text events
              if (result.chunk?.data?.type === "text") {
                const textContent = result.chunk.data.content;

                setMessages((prevMessages) => {
                  const lastMsg = prevMessages[prevMessages.length - 1];
                  let updated: Message[];

                  // Generate a unique ID for the welcome message (only once)
                  const welcomeMessageId = `welcome-${currentThreadId}`;

                  // If last message is the welcome message being built, append to it
                  if (
                    lastMsg?.type === "ai" &&
                    lastMsg.id === welcomeMessageId
                  ) {
                    updated = [
                      ...prevMessages.slice(0, -1),
                      {
                        ...lastMsg,
                        content:
                          ((lastMsg.content as string) || "") + textContent,
                      },
                    ];
                  } else {
                    // Start new welcome message as a regular AI message
                    updated = [
                      ...prevMessages,
                      {
                        id: welcomeMessageId,
                        type: "ai",
                        content: textContent,
                        created_at: new Date().toISOString(),
                      } as Message,
                    ];
                  }

                  // Update session storage with welcome message (strip attachments to avoid quota)
                  if (currentThreadId) {
                    const messagesWithoutAttachments = stripAttachmentsFromMessages(updated);
                    storageManager.setThreadMessages(currentThreadId, messagesWithoutAttachments);
                  }

                  return updated;
                });
              }
            }

          } catch (err) {
            console.error("Failed to create thread:", err);
            throw new Error("Failed to create thread", { cause: err });
          }
        }

        // Apply optimistic updates if provided (for user's message)
        if (config?.optimisticValues && !isNewThread) {
          setMessages((prevMessages) => {
            // Use current state for optimistic update to ensure we have latest messages
            const currentState = { ui, context, messages: prevMessages };
            const optimisticState = config.optimisticValues(currentState);
            if (optimisticState.ui) setUi(optimisticState.ui);
            if (optimisticState.context) setContext(optimisticState.context);
            return optimisticState.messages || prevMessages;
          });
        }

        // Start streaming user's message (currentThreadId is guaranteed to be string here)
        if (!currentThreadId) {
          throw new Error("Thread ID is required");
        }

        // Skip stream if this was just a new thread creation (welcome already sent)
        if (isNewThread) {
          setIsLoading(false);
          setIsSubmitting(false);
          setHasActiveStream(false); // No stream will happen for new thread
          return;
        }


        console.log('[useStreamWithBackend] Starting stream loop...');
        const streamGen = client.stream(currentThreadId, input, config);

        for await (const chunk of streamGen) {

          if (abortController.signal.aborted) {
            break;
          }

          // CRITICAL: Verify this stream still targets the expected thread
          // This prevents stale stream data from updating the wrong thread's messages
          // when user rapidly switches threads during streaming
          if (targetThreadIdRef.current !== currentThreadId) {
            console.log(`[useStreamWithBackend] Thread mismatch detected (target: ${targetThreadIdRef.current}, current: ${currentThreadId}), discarding stream data`);
            break;
          }

          // Skip null chunks
          if (!chunk) {
            continue;
          }

          // Status events are now yielded as 'values' events with separate status message objects
          // No special handling needed here - they'll be processed like any other message in 'values' event

          // Handle different chunk types
          if (chunk.event === "values" && chunk.data) {
            // Merge streamed messages with existing messages
            const incomingMessages = chunk.data.messages || [];

            // Validation logging for two-role model
            for (const msg of incomingMessages) {
              if (msg.role && msg.role !== "user" && msg.role !== "assistant") {
                console.error(
                  `[useStreamWithBackend] Invalid message role received: ${msg.role}`,
                  { id: msg.id, role: msg.role }
                );
              }
            }

            // Update messages and values atomically to prevent race conditions
            // Previously, updatedMessages was captured in a closure which could be stale
            // Now we update both states within the same callback to guarantee consistency
            setMessages((prevMessages) => {
              const updated = [...prevMessages];

              // Process all incoming messages (AI, tool, status, etc.)
              for (const incomingMsg of incomingMessages) {
                // Check if we already have this message
                const existingIndex = updated.findIndex(
                  (m) => m.id === incomingMsg.id,
                );

                if (existingIndex >= 0) {
                  // Update existing message (for streaming AI messages)
                  // Keep it in its original position to maintain chronological order
                  updated[existingIndex] = incomingMsg;
                } else {
                  // New message - insert it in chronological order
                  // Status messages and new AI messages should be appended at the end
                  // This maintains the order: status → AI text (as they arrive from backend)
                  updated.push(incomingMsg);
                }
              }

              // Only write to storage when streaming completes (not during active stream)
              // This eliminates blocking I/O operations during streaming for smoother performance
              if (currentThreadId && !hasActiveStream) {
                const messagesWithoutAttachments = stripAttachmentsFromMessages(updated);
                storageManager.setThreadMessages(currentThreadId, messagesWithoutAttachments);
              }

              // Update ui and context state separately
              if (chunk.data.ui !== undefined) {
                setUi(chunk.data.ui);
              }
              if (chunk.data.context !== undefined) {
                setContext(chunk.data.context);
              }

              return updated;
            });

            // Store metadata for messages (used for branching)
            if (chunk.data.messages) {
              chunk.data.messages.forEach((msg: Message) => {
                if (msg.id && !messagesMetadataRef.current.has(msg.id)) {
                  messagesMetadataRef.current.set(msg.id, {
                    firstSeenState: {
                      messages: chunk.data.messages || [],
                      ui: chunk.data.ui,
                      context: chunk.data.context,
                      parent_checkpoint: chunk.data.checkpoint,
                    },
                  });
                }
              });
            }
          } else if (chunk.event === "metadata") {
            // Handle metadata updates
            if (chunk.data?.thread_id && !currentThreadId) {
              const newThreadId = chunk.data.thread_id as string;
              currentThreadId = newThreadId;
              internalThreadIdRef.current = newThreadId;
              if (onThreadId) {
                onThreadId(newThreadId);
              }
            }
          } else if (chunk.event === "status_change") {
            // Handle status changes immediately for loader animation updates
            if (onStatusChange && chunk.data?.status) {
              onStatusChange(chunk.data.status);
            }
          } else if (chunk.event === "interrupt") {
            setInterrupt(chunk.data);
          } else if (chunk.event === "aborted") {
            // Backend confirmed abort - now we can stop the frontend stream
            console.log("[useStreamWithBackend] Abort confirmed by backend, stopping stream");
            setIsLoading(false);
            setIsSubmitting(false);
            setHasActiveStream(false);
            if (abortControllerRef.current) {
              abortControllerRef.current.abort();
              abortControllerRef.current = null;
            }
            break; // Exit the stream loop
          } else if (
            onCustomEvent &&
            (chunk.event === "custom" || chunk.event === "ui")
          ) {
            // Handle custom events - pass current ui/context for mutation
            onCustomEvent(chunk.data, {
              mutate: (updater) => {
                const currentState = { ui, context };
                const newState = updater(currentState);
                if (newState.ui !== undefined) setUi(newState.ui);
                if (newState.context !== undefined) setContext(newState.context);
              },
            });
          }
        }

        console.log('[useStreamWithBackend] Stream loop ended - setting isLoading=false, isSubmitting=false');
        setIsLoading(false);
        setIsSubmitting(false);
        setHasActiveStream(false); // Mark stream as inactive
        abortControllerRef.current = null;
      } catch (err) {
        if (!abortControllerRef.current?.signal.aborted) {
          console.error("Stream error:", err);
          setError(err instanceof Error ? err : new Error("Stream failed"));
          setIsLoading(false);
          setIsSubmitting(false);
          setHasActiveStream(false); // Mark stream as inactive on error
        }
      }
    },
    [getClient, getThreadId, onThreadId, onCustomEvent, onStatusChange, threadMetadata, ui, context, hasActiveStream],
  );

  // Stop method - request backend to abort streaming
  // The frontend stream stays active until backend sends "aborted" event
  // This ensures we receive all tokens that backend had accumulated
  const stop = useCallback(async () => {
    const threadId = getThreadId();

    // Notify backend to abort (stream stays active to receive final tokens)
    if (threadId && userId) {
      try {
        console.log("[useStreamWithBackend] Sending abort request to backend");
        const client = getClient();
        await client.abortAgent(threadId);
        // Don't abort frontend yet - wait for "aborted" event from backend
        // This allows us to receive any remaining tokens in the queue
      } catch (error) {
        console.warn("[useStreamWithBackend] Failed to send abort signal:", error);
        // Fallback: abort frontend directly on network error
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
        setIsLoading(false);
        setHasActiveStream(false);
      }
    } else {
      // No threadId or userId - just abort frontend
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      setIsLoading(false);
      setHasActiveStream(false);
    }
  }, [getClient, getThreadId, userId]);

  // Clear messages - used when starting new thread
  const clearMessages = useCallback(() => {
    setMessages([]);
    setUi([]);
    setContext({});
    messagesMetadataRef.current.clear();

    // Clear session storage for current thread
    const threadId = getThreadId();
    if (threadId) {
      storageManager.removeThreadMessages(threadId);
      if (STORAGE_CONFIG.ENABLE_STORAGE_WARNINGS) {
        console.log(`🗑️ Cleared session storage for thread ${threadId}`);
      }
    }
  }, [getThreadId]);

  // Get message metadata
  const getMessagesMetadata = useCallback(
    (message: Message): MessageMetadata | undefined => {
      if (!message.id) return undefined;
      return messagesMetadataRef.current.get(message.id);
    },
    [],
  );

  // Set branch (stub for branching support)
  const setBranch = useCallback((branch: string) => {
    // TODO: Implement branching logic
  }, []);

  // Update messages - used for optimistic updates (e.g., tool approvals)
  // messages is the single source of truth, no need to update values
  const updateMessages = useCallback((updater: (prev: Message[]) => Message[]) => {
    setMessages(updater);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create a new project via backend API (calls /llm/create-project)
  // This ensures proper session initialization with context field
  const createProject = useCallback(
    async (conversationName: string, projectId?: string, address?: AddressData | null): Promise<string> => {
      if (!threadMetadata) {
        throw new Error("Cannot create project: threadMetadata is required");
      }

      setIsLoading(true);
      setError(null);

      try {
        const client = getClient();
        const createThreadGen = client.createThread({
          ...threadMetadata,
          conversationName,
          projectId,
          address,
        });

        let newThreadId = "";

        for await (const result of createThreadGen) {
          // First result contains threadId
          if (result.threadId && !newThreadId) {
            newThreadId = result.threadId;
            internalThreadIdRef.current = newThreadId;

            if (onThreadId) {
              onThreadId(newThreadId);
            }

          }

          // Handle welcome message text events
          if (result.chunk?.data?.type === "text") {
            const textContent = result.chunk.data.content;

            setMessages((prevMessages) => {
              const lastMsg = prevMessages[prevMessages.length - 1];
              let updated: Message[];

              // Generate a unique ID for the welcome message (only once)
              const welcomeMessageId = `welcome-${newThreadId}`;

              // If last message is the welcome message being built, append to it
              if (lastMsg?.type === "ai" && lastMsg.id === welcomeMessageId) {
                updated = [
                  ...prevMessages.slice(0, -1),
                  {
                    ...lastMsg,
                    content: ((lastMsg.content as string) || "") + textContent,
                  },
                ];
              } else {
                // Start new welcome message as a regular AI message
                updated = [
                  ...prevMessages,
                  {
                    id: welcomeMessageId,
                    type: "ai",
                    content: textContent,
                    created_at: new Date().toISOString(),
                  } as Message,
                ];
              }

              // Update session storage with welcome message (strip attachments to avoid quota)
              if (newThreadId) {
                const messagesWithoutAttachments = stripAttachmentsFromMessages(updated);
                storageManager.setThreadMessages(newThreadId, messagesWithoutAttachments);
              }

              return updated;
            });
          }
        }

        if (!newThreadId) {
          throw new Error("No thread ID received from backend");
        }

        return newThreadId;
      } catch (err) {
        console.error("Failed to create project:", err);
        const error = err instanceof Error ? err : new Error("Failed to create project");
        setError(error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [getClient, threadMetadata, onThreadId],
  );

  // Expose setter for pending submit state (used by ConversationSelectionGate)
  // This allows setting isSubmitting=true BEFORE navigation, so ChatConvo renders
  // with the correct state on first render (fixes loader not appearing)
  const setPendingSubmit = useCallback((pending: boolean) => {
    console.log('[useStreamWithBackend] setPendingSubmit called with:', pending);
    setIsSubmitting(pending);
  }, []);

  // Memoize return value to prevent unnecessary re-renders of context consumers
  // Without this, every render creates a new object which triggers useMemo recalculation
  // in useStreamSession, causing all context consumers to re-render
  return useMemo(() => ({
    messages,
    ui,
    context,
    isLoading,
    isSubmitting,
    setPendingSubmit,
    error,
    interrupt,
    submit,
    stop,
    clearMessages,
    updateMessages,
    getMessagesMetadata,
    setBranch,
    createProject,
    hasActiveStream,
  }), [
    messages,
    ui,
    context,
    isLoading,
    isSubmitting,
    setPendingSubmit,
    error,
    interrupt,
    submit,
    stop,
    clearMessages,
    updateMessages,
    getMessagesMetadata,
    setBranch,
    createProject,
    hasActiveStream,
  ]);
}
